import { dispatchOnDocumentChange } from '@ps-int/ufo-rocks/lib/helpers/document';
import { PayloadAction } from '@reduxjs/toolkit';
import { Effect } from 'redux-saga/effects';
import {
    delay,
    call as typedCall,
    put as typedPut,
    race as typedRace,
    select as typedSelect,
    take as typedTake,
} from 'typed-redux-saga';

import { NOTIFICATIONS_MAP, notify } from '../../../../shared/features/notifications/ps-notifications';
import { setChatFocus, setChatVisibility } from '../../../../shared/helpers';
import { ACTION, COUNTERS, FEATURE } from '../../../../shared/helpers/logger-enums';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { chatApi, cloudApi } from '../../../../shared/helpers/transport';
import { getChatId, getRoomId, isChatAllowedInConference, isConferenceWithBroadcast } from '../../selectors';
import { ChatState, ConferenceConfig, NotificationId, SidebarContent } from '../../types';
import { setSidebarContent } from '../sidebar/slice';
import {
    exportChatHistorySavedAction,
    getChatHistory,
    joiningTheChatAction,
    setChatHistory,
    setChatId,
    setIsChatLoading,
    setIsChatWidgetReady,
    setJoinedChatId,
    toggleChatVisibilityAction,
} from './slice';

const logger = createLogger('chat-sagas');

/**
 * Добавляет пользователя в чат
 */
export function* joinTheChatSaga(): Generator<Effect, void> {
    try {
        const roomId = yield* typedSelect(getRoomId);
        const userId = yield* typedCall([chatApi, chatApi.getUserChatGuid]);
        yield* typedPut(joiningTheChatAction());

        const { chat_path: chatId } = yield* typedCall([cloudApi, cloudApi.joinToChat], roomId, userId);
        yield* typedPut(setJoinedChatId(chatId));
    } catch (error) {
        yield* typedPut(setJoinedChatId(undefined));

        logger.error(error, {
            method: 'joinTheChatSaga',
        });
    }
}

export function* setOnlineInChat() {
    // после реконнекта мы должны вручную снова добавить пользователя в чат
    const chatAllowed = yield* typedSelect(isChatAllowedInConference);
    const chatId = yield* typedSelect(getChatId);

    if (chatAllowed && chatId) {
        yield* typedCall(joinTheChatSaga);
    }
}

export function* sidebarVisibilityChangeSaga() {
    yield* typedCall(dispatchOnDocumentChange);
}

/**
 *
 * @returns {string|null}
 */
export function* createChatSaga(): Generator<Effect, string | null> {
    try {
        yield* typedPut(setIsChatLoading(true));
        const roomId = yield* typedSelect(getRoomId);
        const user = yield* typedCall([chatApi, chatApi.getUserChatGuid]);

        const chatData = yield* typedCall([cloudApi, cloudApi.createChat], roomId, user);

        yield* typedPut(setChatId(chatData.chat_path));
        countMetrika('meeting_screen', 'chat', 'create');
        const { isChatWidgetReady } = yield* typedRace({
            timeout: delay(10000),
            isChatWidgetReady: typedTake(setIsChatWidgetReady.type),
        });
        yield* typedPut(setIsChatLoading(false));

        if (isChatWidgetReady) {
            yield* typedPut(toggleChatVisibilityAction(true));
        } else {
            yield* typedPut(notify(NOTIFICATIONS_MAP[NotificationId.CHAT_OPEN_ERROR]()));
        }

        logger.endFeatureDelta({
            feature: FEATURE.CHAT,
            action: ACTION.CREATE,
            isSuccess: true,
        });

        return chatData.chat_path;
    } catch (error) {
        logger.error(error, {
            method: 'createChatSaga',
            metrikaName: 'meeting_screen',
            metrikaParams: ['chat', 'error'],
        });
        logger.endFeatureDelta({
            feature: FEATURE.CHAT,
            action: ACTION.CREATE,
            isSuccess: false,
        });

        yield* typedPut(notify(NOTIFICATIONS_MAP[NotificationId.CHAT_CREATE_ERROR]()));

        yield* typedPut(setChatId(null));
        yield* typedPut(setIsChatLoading(false));

        return null;
    }
}

/**
 * Кто-то из других участников создал чат
 *
 * @param {string|null} chatId
 */
export function* clientCreatedChatSaga(chatId: ChatState['chatId']) {
    yield* typedPut(setChatId(chatId));
    yield* typedCall(joinTheChatSaga);
}

/**
 * Экспорт истории чата.
 *
 * @param {string} roomId
 */
export function* exportChatHistorySaga(roomId: ConferenceConfig['room_id']) {
    try {
        // roomId нужно получить заранее, пока мы в ней находимся
        // а вот юзера надо получить по факту перед сохранением истории, так как он мог поменяться за время встречи
        const userId = yield* typedCall([chatApi, chatApi.getUserChatGuid]);

        if (!roomId || !userId) {
            yield* typedPut(setChatHistory(''));
            return;
        }

        const chatHistoryData = yield* typedCall([cloudApi, cloudApi.getChatHistory], roomId, userId);
        const history = chatHistoryData?.history;
        yield* typedPut(setChatHistory(history));
    } catch (error) {
        logger.error(error, {
            method: 'exportChatHistorySaga',
        });
    } finally {
        yield* typedPut(exportChatHistorySavedAction());
    }
}

/**
 * Показываем нотификацию о сохранении истории
 */
export function* saveHistoryNotificationSaga() {
    const chatHistory = yield* typedSelect(getChatHistory);

    if (chatHistory) {
        yield* typedPut(notify(NOTIFICATIONS_MAP[NotificationId.CHAT_HISTORY_SAVE]()));
    }
}

export function* toggleChatVisibilitySaga({ payload: visible }: PayloadAction<boolean>) {
    const isBroadcastChat = yield* typedSelect(isConferenceWithBroadcast);
    const state = visible ? 'open' : 'close';
    const prefix = isBroadcastChat ? 'stream_chat_' : '';
    logger.startDelta(COUNTERS.OPEN_CHAT_DELTA);
    countMetrika('meeting_screen', 'chat', prefix + state);

    setChatVisibility(visible);
    yield* typedPut(setSidebarContent(visible ? SidebarContent.Chat : null));

    if (visible) {
        setChatFocus();
    }
}
